/**
 * Unified Admin Authentication Service
 * 
 * This service integrates the unified magic link authentication system
 * with the admin panel's expected authentication interface.
 */

import { unifiedAuthApi } from './unified-auth-api'
import { AdminUser, AdminRole, LoginForm } from '../types'
import { 
  adaptUserToAdminUser, 
  validateAdminUser, 
  checkUserPermission, 
  checkUserRole,
  UnifiedUser 
} from '../adapters/user-adapter'

export interface UnifiedAuthState {
  user: AdminUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export interface MagicLinkResponse {
  success: boolean
  message?: string
  error?: string
}

export interface AuthTokens {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
}

export interface AuthResponse {
  success: boolean
  message?: string
  error?: string
  user?: AdminUser
}

export class UnifiedAdminAuthService {
  private accessToken: string | null = null
  private currentUser: AdminUser | null = null
  private authStateCallbacks: Set<(user: AdminUser | null, token: string | null) => void> = new Set()

  constructor() {
    // Load existing token from storage
    this.loadTokenFromStorage()
    
    // Development mode: Auto-authenticate if no token exists
    this.initializeDevelopmentAuth()
  }

  /**
   * Subscribe to authentication state changes
   */
  onAuthStateChange(callback: (user: AdminUser | null, token: string | null) => void): () => void {
    this.authStateCallbacks.add(callback)
    return () => this.authStateCallbacks.delete(callback)
  }

  private notifyAuthStateChange(): void {
    this.authStateCallbacks.forEach(callback => {
      try {
        callback(this.currentUser, this.accessToken)
      } catch (error) {
        console.error('Auth state callback error:', error)
      }
    })
  }

  private loadTokenFromStorage(): void {
    try {
      const token = localStorage.getItem('admin_auth_token')
      if (token) {
        this.accessToken = token
      }
    } catch (error) {
      console.error('Failed to load auth token:', error)
    }
  }

  private saveTokenToStorage(token: string): void {
    try {
      localStorage.setItem('admin_auth_token', token)
      this.accessToken = token
      console.log('💾 Token saved to storage:', token.substring(0, 20) + '...')
      // Notify state change after token is saved
      this.notifyAuthStateChange()
    } catch (error) {
      console.error('❌ Failed to save auth token:', error)
    }
  }

  private clearAuth(): void {
    this.accessToken = null
    this.currentUser = null
    localStorage.removeItem('admin_auth_token')
    this.notifyAuthStateChange()
  }

  private async initializeDevelopmentAuth(): Promise<void> {
    // Only run in development mode
    const isDevelopment = (import.meta as any).env?.VITE_NODE_ENV === 'development' || 
                         (import.meta as any).env?.DEV || 
                         window.location.hostname === 'localhost'
    
    if (!isDevelopment) {
      return
    }

    // If no token exists, try to get a development admin token
    if (!this.accessToken) {
      try {
        console.log('🔧 Development mode: Attempting to get admin token...')
        const response = await fetch('/api/auth/dev/admin-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const data = await response.json()
          console.log('✅ Development admin token obtained')
          
          // Set current user from response FIRST
          if (data.user) {
            this.currentUser = {
              id: data.user.id,
              email: data.user.email,
              username: data.user.email,
              role: data.user.admin_role || 'super_admin',
              mfa_enabled: false,
              created_at: new Date().toISOString(),
              last_login: new Date().toISOString()
            }
            console.log('✅ Development admin user set:', this.currentUser?.email)
          }
          
          // Save the token (this will trigger notifyAuthStateChange)
          this.saveTokenToStorage(data.access_token)
          
          console.log('🎯 Development auth complete:', {
            hasUser: !!this.currentUser,
            hasToken: !!this.accessToken,
            userEmail: this.currentUser?.email
          })
        } else {
          console.warn('⚠️ Failed to get development admin token:', response.status)
        }
      } catch (error) {
        console.error('❌ Development auth initialization failed:', error)
      }
    }
  }

  /**
   * Magic link authentication flow - Step 1: Request magic link
   */
  async requestMagicLink(email: string): Promise<MagicLinkResponse> {
    try {
      const result = await unifiedAuthApi.requestMagicLink(email)
      return {
        success: result.success,
        message: result.message || 'Magic link sent! Check your email.',
        error: result.error
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send magic link'
      }
    }
  }

  /**
   * Magic link authentication flow - Step 2: Verify magic link token
   */
  async verifyMagicLink(token: string): Promise<AuthResponse> {
    try {
      console.log('🔍 Unified Admin Auth: Starting verification for token:', token.substring(0, 10) + '...')
      
      const result = await unifiedAuthApi.verifyMagicLink(token)
      
      console.log('🔍 Unified Admin Auth: API result:', { 
        success: result.success, 
        hasUser: !!result.user,
        error: result.error 
      })
      
      if (result.success && result.user) {
        // Validate admin access
        const unifiedUser = result.user as UnifiedUser
        console.log('🔍 Unified Admin Auth: User data:', {
          id: unifiedUser.id,
          email: unifiedUser.email,
          user_type: unifiedUser.user_type,
          admin_role: unifiedUser.admin_role,
          is_active: unifiedUser.is_active,
          is_verified: unifiedUser.is_verified
        })
        
        if (!validateAdminUser(unifiedUser)) {
          console.error('❌ Unified Admin Auth: User validation failed')
          this.clearAuth()
          return {
            success: false,
            error: 'Admin access required. This account does not have administrative privileges.'
          }
        }

        // Convert to AdminUser format and save token
        this.currentUser = adaptUserToAdminUser(unifiedUser)
        console.log('🔍 Unified Admin Auth: Adapted user:', {
          id: this.currentUser.id,
          email: this.currentUser.email,
          role: this.currentUser.role,
          username: this.currentUser.username
        })
        
        // Get the token from the unified auth API
        const accessToken = unifiedAuthApi.getAccessToken()
        if (accessToken) {
          this.saveTokenToStorage(accessToken)
          console.log('✅ Unified Admin Auth: Token saved to storage')
        } else {
          console.warn('⚠️ Unified Admin Auth: No access token available')
        }
        
        this.notifyAuthStateChange()

        return {
          success: true,
          user: this.currentUser,
          message: 'Admin login successful'
        }
      }

      console.error('❌ Unified Admin Auth: Verification failed:', result.error)
      return {
        success: false,
        error: result.error || 'Magic link verification failed'
      }
    } catch (error: any) {
      console.error('❌ Unified Admin Auth: Exception:', error)
      this.clearAuth()
      return {
        success: false,
        error: error.message || 'Authentication failed'
      }
    }
  }

  /**
   * Legacy login interface for compatibility with existing auth store
   * Converts username/password to magic link flow
   */
  async login(credentials: LoginForm): Promise<{ token: string; user: AdminUser }> {
    // For backward compatibility, treat username as email
    const email = credentials.username.includes('@') ? credentials.username : `${credentials.username}@virtualrevamp.com`
    
    // Step 1: Request magic link
    const magicLinkResult = await this.requestMagicLink(email)
    
    if (!magicLinkResult.success) {
      throw new Error(magicLinkResult.error || 'Failed to send magic link')
    }

    // In a real implementation, this would wait for the user to click the magic link
    // For now, we'll throw an error indicating the user needs to check their email
    throw new Error('Please check your email and click the magic link to complete login')
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<AuthResponse> {
    if (!this.accessToken) {
      return {
        success: false,
        error: 'No access token available'
      }
    }

    try {
      const result = await unifiedAuthApi.getCurrentUser()
      
      if (result.success && result.user) {
        // Validate admin access
        if (!validateAdminUser(result.user as UnifiedUser)) {
          this.clearAuth()
          return {
            success: false,
            error: 'Admin access required'
          }
        }

        // Update current user
        const unifiedUser = result.user as any
        this.currentUser = adaptUserToAdminUser(unifiedUser)
        this.notifyAuthStateChange()

        return {
          success: true,
          user: this.currentUser
        }
      }

      return {
        success: false,
        error: result.error || 'Failed to get user data'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get user data'
      }
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await unifiedAuthApi.logout()
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      this.clearAuth()
    }
  }

  /**
   * Refresh token (not needed with magic links, but kept for compatibility)
   */
  async refreshToken(): Promise<{ token: string; user: AdminUser }> {
    try {
      const userResult = await this.getCurrentUser()
      
      if (userResult.success && userResult.user) {
        return {
          token: this.accessToken || '',
          user: userResult.user as AdminUser
        }
      }
    } catch (error) {
      console.error('Token refresh failed, clearing auth:', error)
      this.clearAuth()
    }

    throw new Error('Token refresh failed')
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this.accessToken) {
      return false
    }

    try {
      const userResult = await this.getCurrentUser()
      return userResult.success
    } catch (error) {
      console.error('Authentication check failed:', error)
      // Clear invalid token
      this.clearAuth()
      return false
    }
  }

  /**
   * Check user permission
   */
  checkPermission(permission: string): boolean {
    if (!this.currentUser) {
      return false
    }

    // Convert AdminUser back to UnifiedUser for permission checking
    const unifiedUser: UnifiedUser = {
      id: this.currentUser.id,
      email: this.currentUser.email,
      user_type: 'admin',
      admin_role: this.currentUser.role.toLowerCase(),
      is_active: true,
      is_verified: true,
      created_at: this.currentUser.created_at
    }

    return checkUserPermission(unifiedUser, permission)
  }

  /**
   * Check user role
   */
  hasRole(role: AdminRole): boolean {
    return this.currentUser?.role === role
  }

  /**
   * Get current user
   */
  getUser(): AdminUser | null {
    return this.currentUser
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return this.accessToken
  }

  /**
   * Set auth token (for API client integration)
   */
  setAuthToken(token: string | null): void {
    if (token) {
      console.log('🔑 Setting auth token:', token.substring(0, 20) + '...')
      this.accessToken = token
      try {
        localStorage.setItem('admin_auth_token', token)
        console.log('💾 Token stored in localStorage')
      } catch (error) {
        console.error('❌ Failed to store token in localStorage:', error)
      }
      // Notify state change immediately
      this.notifyAuthStateChange()
    } else {
      console.log('🔑 Clearing auth token')
      this.clearAuth()
    }
  }
}

// Export singleton instance
export const unifiedAdminAuth = new UnifiedAdminAuthService()